<script setup lang="ts">
import { UModalConfirm } from "#components";
import useEventCommerce from "~/composables/events/use-event-commerce";
import useEventSelect from "~/composables/events/use-event-select";
import useSSOLogin from "~/composables/use-sso-login";
import { EVENT_TYPE } from "~/constants";
import EventsAPI from "~/services/events";
import useUserStore from "~/stores/user";
import formatDate from "~/utils/format-date";
import getErrorLocalePathMessage from "~/utils/locale";

export interface UserCardEventProps {
  item: IModelEvent;
  hasBookmark?: boolean;
  hasDelete?: boolean;
  hasBadge?: boolean;
}

const emit = defineEmits(["error", "deleteItem"]);
const props = withDefaults(defineProps<UserCardEventProps>(), {});

const modal = useModal();
const route = useRoute();
const router = useRouter();
const toaster = useToast();
const userStore = useUserStore();
const { t, te, locale } = useI18n();
const appConfig = useRuntimeConfig().public;

const loading = ref(false);

const { executeSocialLogin, socialLoginLoading } = useSSOLogin();
const { commerceType } = useEventCommerce(props.item.commerceCoverage);
const { isSelected, loading: loadingSelectEvent, toggleEventSelect } = useEventSelect(props.item.id);

const isLoggedIn = computed(() => Boolean(userStore.user));
const getFormattedDate = (date: Date): string => {
  const formatStr = locale.value === "ko" ? "MMMM do" : "d MMMM";
  return formatDate(date, formatStr, locale.value as TAvailableLocale);
};
const onError = (error: Error) => {
  const { message } = getErrorLocalePathMessage(error.message, { t, te });
  return toaster.error(message);
};

const isLeaguesView = appConfig.APP_CUSTOMER.enable_leagues_view;
const isHighlightOrUserClip = computed(() => {
  return props.item.eventType === EVENT_TYPE.HIGHLIGHT
    || props.item.eventType === EVENT_TYPE.USER_CLIP
    || props.item.eventType === EVENT_TYPE.USER_HIGHLIGHT;
});

const modalTitle = computed(() => {
  if (props.item.eventType === EVENT_TYPE.USER_CLIP)
    return t("labels.modal_labels.delete_clip");
  else if (props.item.eventType === EVENT_TYPE.USER_HIGHLIGHT)
    return t("labels.modal_labels.delete_highlight");
  else
    return "";
});

const badgeTitle = computed(() => {
  if (props.item.eventType === EVENT_TYPE.USER_CLIP)
    return t("labels.clip", 1);
  else if (props.item.eventType === EVENT_TYPE.USER_HIGHLIGHT || props.item.eventType === EVENT_TYPE.HIGHLIGHT)
    return t("labels.highlights", 0);
  else
    return "";
});

const bookmarkTooltipTitle = computed(() => {
  if (isSelected.value)
    return t("labels.remove_from_selected");
  else
    return t("labels.add_to_selected");
});

const link = computed(() => isHighlightOrUserClip.value
  ? { path: `/events/${props.item.eventId}/`, query: { type: props.item.eventType, mediaId: props.item.id } }
  : { path: `/events/${props.item.id}/` },
);

async function onClickBookmark() {
  if (!isLoggedIn.value) {
    if (appConfig.APP_FLAGS.hasIfaAuth && appConfig.APP_CUSTOMER.app_config.enable_skip_login_page_for_ifa_sso)
      return executeSocialLogin("ifa");

    return router.push({ path: "/auth/login/", query: { redirectUrl: route.fullPath } });
  }

  toggleEventSelect();
}

function onClickDelete(item: IModelEvent) {
  loading.value = true;

  modal.open(UModalConfirm, {
    heading: modalTitle.value,
    icon: item.eventType === EVENT_TYPE.CLIP || item.eventType === EVENT_TYPE.USER_CLIP ? "clip" : "highlight",
    confirmLabel: t("button.delete"),
    variant: "delete",
    confirmFn: () => {
      return EventsAPI.userMedia
        .deleteUserClip(item.id)
        .then(() => emit("deleteItem", item))
        .catch(onError)
        .finally(() => {
          loading.value = false;
        });
    },
  });
}
</script>

<template>
  <BaseButton
    tag="nuxt-link"
    :name="`item-event-${props.item.id}`"
    type="icon"
    :to="link"
    class="card-item-link"
  >
    <CardEvent
      :title="props.item.metaTitle"
      :has-lock="!props.item.isEventAvailableForUser"
      :has-team-logos="isLeaguesView"
      :has-badge="props.hasBadge"
      :has-bookmark="props.hasBookmark"
      :has-bookmark-disabled="loadingSelectEvent || socialLoginLoading"
      :has-delete="props.hasDelete"
      :has-delete-disabled="loading"
      :has-vidswap-stats="props.item.isEnabledVidswapStats"
      :genius-id="props.item.geniusId"
      :image-url="props.item.imageUrl"
      :sport-type="props.item.sportType"
      :team1-name="props.item.team1Name"
      :team2-name="props.item.team2Name"
      :team1-score="props.item.team1Score"
      :team2-score="props.item.team2Score"
      :team1-color="props.item.team1Color"
      :team2-color="props.item.team2Color"
      :team1-logo-url="props.item.team1LogoUrl"
      :team2-logo-url="props.item.team2LogoUrl"
      :is-bookmark-active="isSelected"
      :event-type="props.item.eventType"
      :date="getFormattedDate(props.item.gameStartDate as Date)"
      :commerce-type="commerceType"
      :modal-title="modalTitle"
      :badge-title="badgeTitle"
      :modal-cancel-btn-title="t('button.cancel')"
      :modal-remove-btn-title="t('button.remove')"
      :bookmark-tooltip-title="bookmarkTooltipTitle"
      :game-type="props.item.gameType"
      :gender="props.item.gender"
      @click-bookmark="onClickBookmark()"
      @click-delete="onClickDelete(props.item)"
    />
  </BaseButton>
</template>
